import { request } from "umi";
import { paramsSerializer } from "../../api";

const urlPrefix = "/api/tenant/zulip/channels";

/**
 * List Zulip channels — proxied live from Zulip, no local pagination.
 *
 * GET /api/tenant/zulip/channels
 */
export async function getZulipChannels() {
  return request<API.ResultObject<API.ZulipChannel[]>>(`${urlPrefix}`, {
    method: "GET",
    withToken: true,
    paramsSerializer,
  }).then((res) => res.data ?? []);
}

/**
 * Create a Zulip channel.
 *
 * POST /api/tenant/zulip/channels
 */
export async function createZulipChannel(data: { name: string; description?: string; user_ids?: number[]; invite_only?: boolean }) {
  return request<API.ResultObject<any>>(`${urlPrefix}`, {
    method: "POST",
    data,
  }).then((res) => res.data);
}

/**
 * Rename a Zulip channel. oldName is sent purely so the backend can write a
 * readable old->new audit diff — Zulip's own rename response doesn't echo it.
 *
 * PUT /api/tenant/zulip/channels/{id}
 */
export async function renameZulipChannel(streamId: number, newName: string, oldName?: string) {
  return request<API.ResultObject<any>>(`${urlPrefix}/${streamId}`, {
    method: "PUT",
    data: { new_name: newName, old_name: oldName },
  }).then((res) => res.data);
}

/**
 * Archive a Zulip channel. name is sent purely for a readable audit entry.
 *
 * DELETE /api/tenant/zulip/channels/{id}
 */
export async function archiveZulipChannel(streamId: number, name?: string) {
  return request<API.ResultObject<any>>(`${urlPrefix}/${streamId}`, {
    method: "DELETE",
    params: { name },
    paramsSerializer,
  }).then((res) => res.data);
}

/**
 * Current subscribers, resolved to DispatchButler identities.
 *
 * GET /api/tenant/zulip/channels/{id}/subscribers
 */
export async function getZulipChannelSubscribers(streamId: number) {
  return request<API.ResultObject<API.ZulipChannelMember[]>>(`${urlPrefix}/${streamId}/subscribers`, {
    method: "GET",
    withToken: true,
  }).then((res) => res.data ?? []);
}

/**
 * Add and/or remove subscribers (DispatchButler user ids). Returns the
 * refreshed subscriber list.
 *
 * PUT /api/tenant/zulip/channels/{id}/subscribers
 */
export async function updateZulipChannelSubscribers(streamId: number, name: string, changes: { add?: number[]; remove?: number[] }) {
  return request<API.ResultObject<API.ZulipChannelMember[]>>(`${urlPrefix}/${streamId}/subscribers`, {
    method: "PUT",
    data: { name, ...changes },
  }).then((res) => res.data ?? []);
}

/**
 * Reassign which channel folder a channel belongs to (Zulip's only
 * per-channel grouping — channels have no manual order of their own, only
 * folder membership; see channelFolders' order for the thing that IS
 * orderable). old/new folder names are sent purely for a readable audit
 * diff, same reasoning as renameZulipChannel's oldName.
 *
 * PUT /api/tenant/zulip/channels/{id}/folder
 */
export async function assignZulipChannelFolder(
  streamId: number,
  folderId: number | null,
  oldFolderName?: string | null,
  newFolderName?: string | null,
) {
  return request<API.ResultObject<any>>(`${urlPrefix}/${streamId}/folder`, {
    method: "PUT",
    data: { folder_id: folderId, old_folder_name: oldFolderName, new_folder_name: newFolderName },
  }).then((res) => res.data);
}

/**
 * Best-effort audit log for a channel created via the Chat panel's own
 * direct-to-Zulip flow (NewChannelModal.tsx) — that call never touches this
 * backend, so without this there would be no audit_trail record of it.
 *
 * POST /api/tenant/zulip/channels/{id}/log-created
 */
export async function logZulipChannelCreated(streamId: number, data: { name: string; invite_only?: boolean }) {
  return request<API.ResultObject<any>>(`${urlPrefix}/${streamId}/log-created`, {
    method: "POST",
    data,
  }).catch(() => {
    // Best-effort — the channel itself was already created successfully in Zulip either way.
  });
}

/**
 * Best-effort audit log for a topic created via the Chat panel (a new topic
 * is just the first message sent with a topic name Zulip hasn't seen on this
 * channel before — see sendStreamMessage in chatApi.ts).
 *
 * POST /api/tenant/zulip/channels/{id}/log-topic-created
 */
export async function logZulipTopicCreated(streamId: number, topic: string) {
  return request<API.ResultObject<any>>(`${urlPrefix}/${streamId}/log-topic-created`, {
    method: "POST",
    data: { topic },
  }).catch(() => {
    // Best-effort — the message itself already sent successfully either way.
  });
}

/**
 * Best-effort audit log for InviteMembersModal.tsx's direct-to-Zulip invite
 * call. zulipUserIds are Zulip's own user ids (that modal's UserPicker works
 * in Zulip's id space, not DispatchButler's — see UserPicker.tsx).
 *
 * POST /api/tenant/zulip/channels/{id}/log-invited
 */
export async function logZulipInvited(streamId: number, zulipUserIds: number[]) {
  return request<API.ResultObject<any>>(`${urlPrefix}/${streamId}/log-invited`, {
    method: "POST",
    data: { zulip_user_ids: zulipUserIds },
  }).catch(() => {
    // Best-effort — the invite itself already went through successfully either way.
  });
}
